import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Typography,
  Divider,
  Tooltip,
  OutlinedInput,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon, FilterAltOff as FilterAltOffIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  getMaterialFamilies,
  getMaterialGroups,
  getMaterialSubgroups,
  getMaterialList,
  getMaterialDetails,
  type IMaterialDetail,
} from "../../../services/businessCaseService";
import type { IMaterialSubmission } from "../../../services/businessCaseService";
import { useTranslation } from "react-i18next";
import type { TranslationKeys } from "../../../i18n";

interface MaterialFormTableProps {
  rows: IMaterialSubmission[];
  onChange: (updatedRows: IMaterialSubmission[]) => void;
}

interface DropdownState {
  open: boolean;
  loading: boolean;
  search: string;
  page: number;
  hasMore: boolean;
  totalCount: number;
}

const MaterialFormTable: React.FC<MaterialFormTableProps> = ({
  rows,
  onChange,
}) => {
  const { t } = useTranslation<TranslationKeys>();

  // Selection state
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedSubgroups, setSelectedSubgroups] = useState<string[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);

  // Table search state
  const [tableFamilySearch, setTableFamilySearch] = useState<string>("");
  const [tableGroupSearch, setTableGroupSearch] = useState<string>("");
  const [tableSubgroupSearch, setTableSubgroupSearch] = useState<string>("");
  const [tableMaterialSearch, setTableMaterialSearch] = useState<string>("");

  // Options state
  const [familyOptions, setFamilyOptions] = useState<string[]>([]);
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [subgroupOptions, setSubgroupOptions] = useState<string[]>([]);
  const [materialOptions, setMaterialOptions] = useState<IMaterialDetail[]>([]);

  // Dropdown states
  const [familyDropdown, setFamilyDropdown] = useState<DropdownState>({
    open: false,
    loading: false,
    search: "",
    page: 1,
    hasMore: true,
    totalCount: 0,
  });

  const [groupDropdown, setGroupDropdown] = useState<DropdownState>({
    open: false,
    loading: false,
    search: "",
    page: 1,
    hasMore: true,
    totalCount: 0,
  });

  const [subgroupDropdown, setSubgroupDropdown] = useState<DropdownState>({
    open: false,
    loading: false,
    search: "",
    page: 1,
    hasMore: true,
    totalCount: 0,
  });

  const [materialDropdown, setMaterialDropdown] = useState<DropdownState>({
    open: false,
    loading: false,
    search: "",
    page: 1,
    hasMore: true,
    totalCount: 0,
  });

  // Debounce timer refs
  const familySearchTimer = useRef<NodeJS.Timeout | null>(null);
  const groupSearchTimer = useRef<NodeJS.Timeout | null>(null);
  const subgroupSearchTimer = useRef<NodeJS.Timeout | null>(null);
  const materialSearchTimer = useRef<NodeJS.Timeout | null>(null);

  // 🔥 Refs to track auto-population and forced reload states
  const isAutoPopulatingRef = useRef(false);
  const shouldForceReloadMaterialsRef = useRef(false);
  const materialScrollPositionRef = useRef<number>(0);

  // Load families
  const loadFamilies = useCallback(
    async (search: string = "", page: number = 1, append: boolean = false) => {
      if (!append) {
        setFamilyDropdown((prev) => ({ ...prev, loading: true }));
      }

      try {
        const response = await getMaterialFamilies(
          search,
          page,
          100
        );

        setFamilyDropdown((prev) => ({
          ...prev,
          loading: false,
          page: response.page,
          hasMore: response.page < response.total_pages,
          totalCount: response.count,
        }));

        if (append) {
          setFamilyOptions((prev) => [...prev, ...response.results]);
        } else {
          setFamilyOptions(response.results);
        }
      } catch (error) {
        console.error("Error loading families:", error);
        setFamilyDropdown((prev) => ({ ...prev, loading: false }));
        toast.error("Failed to load families");
      }
    },
    []
  );

  // Load groups with proper filtering
  const loadGroups = useCallback(
    async (search: string = "", page: number = 1, append: boolean = false) => {
      if (!append) {
        setGroupDropdown((prev) => ({ ...prev, loading: true }));
      }

      try {
        const response = await getMaterialGroups(
          selectedFamilies.length > 0 ? selectedFamilies : undefined,
          search,
          page,
          100
        );

        setGroupDropdown((prev) => ({
          ...prev,
          loading: false,
          page: response.page,
          hasMore: response.page < response.total_pages,
          totalCount: response.count,
        }));

        if (append) {
          setGroupOptions((prev) => [...prev, ...response.results]);
        } else {
          setGroupOptions(response.results);
        }
      } catch (error) {
        console.error("Error loading groups:", error);
        setGroupDropdown((prev) => ({ ...prev, loading: false }));
        toast.error("Failed to load groups");
      }
    },
    [selectedFamilies]
  );

  // Load subgroups with proper filtering
  const loadSubgroups = useCallback(
    async (search: string = "", page: number = 1, append: boolean = false) => {
      if (!append) {
        setSubgroupDropdown((prev) => ({ ...prev, loading: true }));
      }

      try {
        const response = await getMaterialSubgroups(
          selectedFamilies.length > 0 ? selectedFamilies : undefined,
          selectedGroups.length > 0 ? selectedGroups : undefined,
          search,
          page,
          100
        );

        setSubgroupDropdown((prev) => ({
          ...prev,
          loading: false,
          page: response.page,
          hasMore: response.page < response.total_pages,
          totalCount: response.count,
        }));

        if (append) {
          setSubgroupOptions((prev) => [...prev, ...response.results]);
        } else {
          setSubgroupOptions(response.results);
        }
      } catch (error) {
        console.error("Error loading subgroups:", error);
        setSubgroupDropdown((prev) => ({ ...prev, loading: false }));
        toast.error("Failed to load subgroups");
      }
    },
    [selectedFamilies, selectedGroups]
  );

  // Load materials with proper filtering
  const loadMaterials = useCallback(
    async (search: string = "", page: number = 1, append: boolean = false) => {
      if (!append) {
        setMaterialDropdown((prev) => ({ ...prev, loading: true }));
      }

      try {
        const response = await getMaterialList(
          selectedFamilies.length > 0 ? selectedFamilies : undefined,
          selectedGroups.length > 0 ? selectedGroups : undefined,
          selectedSubgroups.length > 0 ? selectedSubgroups : undefined,
          search,
          page,
          100
        );

        setMaterialDropdown((prev) => ({
          ...prev,
          loading: false,
          page: response.page,
          hasMore: response.page < response.total_pages,
          totalCount: response.count,
        }));

        if (append) {
          setMaterialOptions((prev) => [...prev, ...response.results]);
        } else {
          setMaterialOptions(response.results);
        }
      } catch (error) {
        console.error("Error loading materials:", error);
        setMaterialDropdown((prev) => ({ ...prev, loading: false }));
        toast.error("Failed to load materials");
      }
    },
    [selectedFamilies, selectedGroups, selectedSubgroups]
  );

  // 🔥 FIXED: Only reload when dropdown is open AND not auto-populating
  useEffect(() => {
    if (groupDropdown.open && !isAutoPopulatingRef.current) {
      setGroupOptions([]);
      setGroupDropdown((prev) => ({ ...prev, page: 1 }));
      loadGroups(groupDropdown.search, 1, false);
    }
  }, [selectedFamilies, groupDropdown.open, loadGroups]);

  useEffect(() => {
    if (subgroupDropdown.open && !isAutoPopulatingRef.current) {
      setSubgroupOptions([]);
      setSubgroupDropdown((prev) => ({ ...prev, page: 1 }));
      loadSubgroups(subgroupDropdown.search, 1, false);
    }
  }, [selectedFamilies, selectedGroups, subgroupDropdown.open, loadSubgroups]);

  useEffect(() => {
    // Reload materials when filters change AND:
    // 1. Dropdown is open
    // 2. Either it's not auto-populating OR we explicitly need to force reload
    if (materialDropdown.open && (!isAutoPopulatingRef.current || shouldForceReloadMaterialsRef.current)) {
      // Reset the force reload flag
      shouldForceReloadMaterialsRef.current = false;
      
      setMaterialOptions([]);
      setMaterialDropdown((prev) => ({ ...prev, page: 1 }));
      loadMaterials(materialDropdown.search, 1, false);
    }
  }, [
    selectedFamilies,
    selectedGroups,
    selectedSubgroups,
    materialDropdown.open,
    loadMaterials,
  ]);

  // Initial load when dropdown opens
  useEffect(() => {
    if (familyDropdown.open && familyOptions.length === 0) {
      loadFamilies();
    }
  }, [familyDropdown.open, familyOptions.length, loadFamilies]);

  // Search handlers with debounce
  const handleFamilySearch = useCallback(
    (value: string) => {
      setFamilyDropdown((prev) => ({ ...prev, search: value }));

      if (familySearchTimer.current) {
        clearTimeout(familySearchTimer.current);
      }

      familySearchTimer.current = setTimeout(() => {
        setFamilyOptions([]);
        setFamilyDropdown((prev) => ({ ...prev, page: 1 }));
        loadFamilies(value, 1, false);
      }, 300);
    },
    [loadFamilies]
  );

  const handleGroupSearch = useCallback(
    (value: string) => {
      setGroupDropdown((prev) => ({ ...prev, search: value }));

      if (groupSearchTimer.current) {
        clearTimeout(groupSearchTimer.current);
      }

      groupSearchTimer.current = setTimeout(() => {
        setGroupOptions([]);
        setGroupDropdown((prev) => ({ ...prev, page: 1 }));
        loadGroups(value, 1, false);
      }, 300);
    },
    [loadGroups]
  );

  const handleSubgroupSearch = useCallback(
    (value: string) => {
      setSubgroupDropdown((prev) => ({ ...prev, search: value }));

      if (subgroupSearchTimer.current) {
        clearTimeout(subgroupSearchTimer.current);
      }

      subgroupSearchTimer.current = setTimeout(() => {
        setSubgroupOptions([]);
        setSubgroupDropdown((prev) => ({ ...prev, page: 1 }));
        loadSubgroups(value, 1, false);
      }, 300);
    },
    [loadSubgroups]
  );

  const handleMaterialSearch = useCallback(
    (value: string) => {
      setMaterialDropdown((prev) => ({ ...prev, search: value }));

      if (materialSearchTimer.current) {
        clearTimeout(materialSearchTimer.current);
      }

      materialSearchTimer.current = setTimeout(() => {
        setMaterialOptions([]);
        setMaterialDropdown((prev) => ({ ...prev, page: 1 }));
        loadMaterials(value, 1, false);
      }, 300);
    },
    [loadMaterials]
  );

  // Load more handlers
  const handleLoadMoreFamilies = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (familyDropdown.hasMore && !familyDropdown.loading) {
      loadFamilies(familyDropdown.search, familyDropdown.page + 1, true);
    }
  };

  const handleLoadMoreGroups = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (groupDropdown.hasMore && !groupDropdown.loading) {
      loadGroups(groupDropdown.search, groupDropdown.page + 1, true);
    }
  };

  const handleLoadMoreSubgroups = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (subgroupDropdown.hasMore && !subgroupDropdown.loading) {
      loadSubgroups(subgroupDropdown.search, subgroupDropdown.page + 1, true);
    }
  };

  const handleLoadMoreMaterials = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (materialDropdown.hasMore && !materialDropdown.loading) {
      loadMaterials(materialDropdown.search, materialDropdown.page + 1, true);
    }
  };

  // Auto-populate parent family from materials
  const autoPopulateParentsFromMaterials = useCallback(
    async (materials: string[]) => {
      if (materials.length === 0) return;

      try {
        const details = await getMaterialDetails(materials);
        const detailsArray = Object.values(details);

        const families = Array.from(
          new Set(detailsArray.map((d) => d.product_family))
        );
        const groups = Array.from(
          new Set(detailsArray.map((d) => d.product_group))
        );
        const subgroups = Array.from(
          new Set(detailsArray.map((d) => d.product_subgroup))
        );

        setSelectedFamilies((prev) =>
          Array.from(new Set([...prev, ...families]))
        );
        setSelectedGroups((prev) => Array.from(new Set([...prev, ...groups])));
        setSelectedSubgroups((prev) =>
          Array.from(new Set([...prev, ...subgroups]))
        );
      } catch (error) {
        console.error("Error auto-populating parents from materials:", error);
      }
    },
    []
  );

  // Auto-populate family & group from subgroups
  const autoPopulateParentsFromSubgroups = useCallback(
    async (subgroups: string[]) => {
      if (subgroups.length === 0) return;

      isAutoPopulatingRef.current = true;

      try {
        const response = await getMaterialList(
          undefined,
          undefined,
          subgroups,
          "",
          1,
          100
        );

        if (response.results.length > 0) {
          const families = Array.from(
            new Set(response.results.map((m) => m.product_family))
          );
          const groups = Array.from(
            new Set(response.results.map((m) => m.product_group))
          );

          setSelectedFamilies((prev) =>
            Array.from(new Set([...prev, ...families]))
          );
          setSelectedGroups((prev) =>
            Array.from(new Set([...prev, ...groups]))
          );
        }
      } catch (error) {
        console.error("Error auto-populating from subgroups:", error);
      } finally {
        setTimeout(() => {
          isAutoPopulatingRef.current = false;
        }, 100);
      }
    },
    []
  );

  // Auto-populate family from groups
  const autoPopulateParentsFromGroups = useCallback(
    async (groups: string[]) => {
      if (groups.length === 0) return;

      isAutoPopulatingRef.current = true;

      try {
        const response = await getMaterialList(
          undefined,
          groups,
          undefined,
          "",
          1,
          100
        );

        if (response.results.length > 0) {
          const families = Array.from(
            new Set(response.results.map((m) => m.product_family))
          );
          setSelectedFamilies((prev) =>
            Array.from(new Set([...prev, ...families]))
          );
        }
      } catch (error) {
        console.error("Error auto-populating from groups:", error);
      } finally {
        setTimeout(() => {
          isAutoPopulatingRef.current = false;
        }, 100);
      }
    },
    []
  );

  // Family change handler with cascading deselection
  const handleFamilyChange = useCallback(
    (families: string[]) => {
      setSelectedFamilies(families);

      // Cascading deselection: remove all child selections if parent removed
      const removedFamilies = selectedFamilies.filter(
        (f) => !families.includes(f)
      );

      if (removedFamilies.length > 0) {
        // Clear all child selections when family is removed
        setSelectedGroups([]);
        setSelectedSubgroups([]);
        setSelectedMaterials([]);

        // Clear cached options to force reload
        setGroupOptions([]);
        setSubgroupOptions([]);
        setMaterialOptions([]);
      }
    },
    [selectedFamilies]
  );

  // Group change handler with auto-selection & cascading
  const handleGroupChange = useCallback(
    async (groups: string[]) => {
      const newGroups = groups.filter((g) => !selectedGroups.includes(g));
      const removedGroups = selectedGroups.filter((g) => !groups.includes(g));

      // Auto-populate parent families for new groups
      if (newGroups.length > 0) {
        await autoPopulateParentsFromGroups(newGroups);
      }

      setSelectedGroups(groups);

      // Cascading deselection: remove child selections
      if (removedGroups.length > 0) {
        setSelectedSubgroups([]);
        setSelectedMaterials([]);
        setSubgroupOptions([]);
        setMaterialOptions([]);
      }
    },
    [selectedGroups, autoPopulateParentsFromGroups]
  );

  // Subgroup change handler with auto-selection & cascading
  const handleSubgroupChange = useCallback(
    async (subgroups: string[]) => {
      const newSubgroups = subgroups.filter(
        (s) => !selectedSubgroups.includes(s)
      );
      const removedSubgroups = selectedSubgroups.filter(
        (s) => !subgroups.includes(s)
      );

      // Auto-populate parent families and groups for new subgroups
      if (newSubgroups.length > 0) {
        await autoPopulateParentsFromSubgroups(newSubgroups);
      }

      setSelectedSubgroups(subgroups);

      // Cascading deselection: remove material selections
      if (removedSubgroups.length > 0) {
        setSelectedMaterials([]);
        setMaterialOptions([]);
      }
    },
    [selectedSubgroups, autoPopulateParentsFromSubgroups]
  );

  // Material change handler with auto-selection of all parents
  const handleMaterialChange = useCallback(
    async (materials: string[]) => {
      const newMaterials = materials.filter(
        (m) => !selectedMaterials.includes(m)
      );

      setSelectedMaterials(materials);

      // 🔥 FIXED: Only auto-populate and reload on FIRST material selection
      // After first selection, subgroup is already selected, no need to filter again
      const isFirstMaterialSelection = selectedMaterials.length === 0 && newMaterials.length > 0;

      // Auto-populate all parent levels for new materials (only on first selection)
      if (isFirstMaterialSelection && materialDropdown.open) {
        // Set flag to prevent normal useEffect from interfering during auto-population
        isAutoPopulatingRef.current = true;
        
        try {
          // Wait for auto-population to complete
          await autoPopulateParentsFromMaterials(newMaterials);
          
          // 🔥 Set flag to force reload materials in useEffect
          // This will trigger the useEffect to reload materials with the new hierarchy filters
          shouldForceReloadMaterialsRef.current = true;
        } finally {
          // Reset flag after a short delay to allow useEffect to trigger
          setTimeout(() => {
            isAutoPopulatingRef.current = false;
          }, 50);
        }
      }
    },
    [selectedMaterials, autoPopulateParentsFromMaterials, materialDropdown.open]
  );

  // Add materials to table
  const handleAdd = useCallback(() => {
    if (selectedMaterials.length === 0) {
      toast.warning("Please select at least one material");
      return;
    }

    const existingMaterials = new Set(rows.map((row) => row.material));
    const newMaterials = selectedMaterials.filter(
      (m) => !existingMaterials.has(m)
    );

    if (newMaterials.length === 0) {
      toast.warning("All selected materials are already in the table");
      return;
    }

    const newRows: IMaterialSubmission[] = newMaterials
      .map((materialCode) => {
        const detail = materialOptions.find((m) => m.material === materialCode);
        if (!detail) return null;

        return {
          productFamily: detail.product_family,
          productGroup: detail.product_group,
          productSubGroup: detail.product_subgroup,
          material: detail.material,
        };
      })
      .filter((row): row is IMaterialSubmission => row !== null);

    onChange([...rows, ...newRows]);

    if (newMaterials.length < selectedMaterials.length) {
      const duplicateCount = selectedMaterials.length - newMaterials.length;
      toast.info(
        `Added ${newMaterials.length} material(s). ${duplicateCount} duplicate(s) skipped.`
      );
    } else {
      toast.success(`Added ${newMaterials.length} material(s)`);
    }

    // Reset all selections
    setSelectedFamilies([]);
    setSelectedGroups([]);
    setSelectedSubgroups([]);
    setSelectedMaterials([]);
  }, [selectedMaterials, rows, materialOptions, onChange]);

  // Delete row
  const handleDeleteRow = useCallback(
    (index: number) => {
      const updatedRows = rows.filter((_, i) => i !== index);
      onChange(updatedRows);
    },
    [rows, onChange]
  );

  // Filter rows based on table search
  const filteredRows = rows.filter((row) => {
    const familyMatch = row.productFamily?.toLowerCase().includes(tableFamilySearch.toLowerCase()) ?? true;
    const groupMatch = row.productGroup?.toLowerCase().includes(tableGroupSearch.toLowerCase()) ?? true;
    const subgroupMatch = row.productSubGroup?.toLowerCase().includes(tableSubgroupSearch.toLowerCase()) ?? true;
    const materialMatch = row.material?.toLowerCase().includes(tableMaterialSearch.toLowerCase()) ?? true;
    return familyMatch && groupMatch && subgroupMatch && materialMatch;
  });

  // Clear individual dropdown handlers
  const handleClearFamily = useCallback(() => {
    setSelectedFamilies([]);
    setSelectedGroups([]);
    setSelectedSubgroups([]);
    setSelectedMaterials([]);
    setGroupOptions([]);
    setSubgroupOptions([]);
    setMaterialOptions([]);
  }, []);

  const handleClearGroup = useCallback(() => {
    setSelectedGroups([]);
    setSelectedSubgroups([]);
    setSelectedMaterials([]);
    setSubgroupOptions([]);
    setMaterialOptions([]);
  }, []);

  const handleClearSubgroup = useCallback(() => {
    setSelectedSubgroups([]);
    setSelectedMaterials([]);
    setMaterialOptions([]);
  }, []);

  const handleClearMaterial = useCallback(() => {
    setSelectedMaterials([]);
  }, []);

  const handleClearAll = useCallback(() => {
    setSelectedFamilies([]);
    setSelectedGroups([]);
    setSelectedSubgroups([]);
    setSelectedMaterials([]);
    setFamilyOptions([]);
    setGroupOptions([]);
    setSubgroupOptions([]);
    setMaterialOptions([]);
  }, []);

  // Select All handler for materials only
  const handleSelectAllMaterials = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if we should deselect all or select all
    const shouldSelectAll = selectedMaterials.length !== materialDropdown.totalCount;
    
    if (!shouldSelectAll) {
      // Deselect all
      setSelectedMaterials([]);
      return;
    }
    
    // 🔥 NEW: Warn user if trying to select all without filters when count is very large
    const hasNoFilters = selectedFamilies.length === 0 && 
                        selectedGroups.length === 0 && 
                        selectedSubgroups.length === 0;
    
    if (hasNoFilters && materialDropdown.totalCount > 50000) {
      toast.warning(
        `Cannot select all ${materialDropdown.totalCount.toLocaleString()} materials without filters. ` +
        `Please select at least one Family, Group, or Subgroup to filter materials first.`,
        { autoClose: 6000 }
      );
      return;
    }
    
    if (hasNoFilters && materialDropdown.totalCount > 10000) {
      const confirmed = window.confirm(
        `Warning: You are about to select ${materialDropdown.totalCount.toLocaleString()} materials without any filters. ` +
        `This may take a while and could slow down your browser.\n\n` +
        `We recommend selecting upper hierarchies (Family/Group/Subgroup) first to filter materials.\n\n` +
        `Do you want to continue?`
      );
      
      if (!confirmed) {
        return;
      }
    }
    
    // Select all: fetch ALL materials from backend without limit
    setMaterialDropdown((prev) => ({ ...prev, loading: true }));
    isAutoPopulatingRef.current = true;
    
    try {
      // 🔥 UPDATED: Fetch ALL materials by requesting a very large page size
      // This gets around pagination limits
      const response = await getMaterialList(
        selectedFamilies.length > 0 ? selectedFamilies : undefined,
        selectedGroups.length > 0 ? selectedGroups : undefined,
        selectedSubgroups.length > 0 ? selectedSubgroups : undefined,
        materialDropdown.search,
        1,
        999999 // Very large page size to get all materials
      );
      
      const allMaterialValues = response.results.map((opt) => opt.material);
      setSelectedMaterials(allMaterialValues);
      
      // 🔥 NEW: Auto-populate all parent hierarchies from the fetched materials
      if (response.results.length > 0) {
        const families = Array.from(
          new Set(response.results.map((m) => m.product_family))
        );
        const groups = Array.from(
          new Set(response.results.map((m) => m.product_group))
        );
        const subgroups = Array.from(
          new Set(response.results.map((m) => m.product_subgroup))
        );

        setSelectedFamilies((prev) =>
          Array.from(new Set([...prev, ...families]))
        );
        setSelectedGroups((prev) => Array.from(new Set([...prev, ...groups])));
        setSelectedSubgroups((prev) =>
          Array.from(new Set([...prev, ...subgroups]))
        );
      }
      
      // Update options to include all fetched materials
      setMaterialOptions(response.results);
      setMaterialDropdown((prev) => ({
        ...prev,
        loading: false,
        page: 1,
        hasMore: false,
        totalCount: response.count,
      }));
      
      toast.success(`Selected all ${allMaterialValues.length} materials`);
    } catch (error) {
      console.error("Error selecting all materials:", error);
      setMaterialDropdown((prev) => ({ ...prev, loading: false }));
      toast.error("Failed to select all materials");
    } finally {
      setTimeout(() => {
        isAutoPopulatingRef.current = false;
      }, 100);
    }
  }, [selectedMaterials.length, materialDropdown.totalCount, materialDropdown.search, selectedFamilies, selectedGroups, selectedSubgroups]);

  // 🔥 FIXED: Custom dropdown render with scroll preservation
  const renderDropdown = (
    label: string,
    options: string[] | IMaterialDetail[],
    selected: string[],
    onToggle: (value: string) => void,
    dropdownState: DropdownState,
    setDropdownState: React.Dispatch<React.SetStateAction<DropdownState>>,
    onSearch: (value: string) => void,
    onLoadMore: (e: React.MouseEvent) => void,
    isMaterial: boolean = false,
    flexBasis: string,
    onSelectAll?: (e: React.MouseEvent) => void,
    onClear?: () => void
  ) => {
    const isOpen = dropdownState.open;
    const currentPage = dropdownState.page;
    const totalPages = Math.ceil(dropdownState.totalCount / 100);
    const currentCount = options.length;

    // Calculate Select All state (only for material dropdown)
    // For materials, check against total count from backend instead of just loaded options
    const allValues = isMaterial
      ? (options as IMaterialDetail[]).map((opt) => opt.material)
      : (options as string[]);
    const allSelected = isMaterial 
      ? selected.length === dropdownState.totalCount && dropdownState.totalCount > 0
      : allValues.length > 0 && selected.length === allValues.length;
    const someSelected = isMaterial
      ? selected.length > 0 && selected.length < dropdownState.totalCount
      : selected.length > 0 && selected.length < allValues.length;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexBasis, gap: 0.5 }}>
        <FormControl
          size="small"
          sx={{ flex: 1, minWidth: 0, position: 'relative' }}
          variant="outlined"
        >
          <InputLabel>
            {label} ({selected.length} selected)
          </InputLabel>
          <Select
            multiple
            value={selected}
            label={`${label} (${selected.length} selected)`}
            open={isOpen}
            onOpen={() => {
              setDropdownState((prev) => ({ ...prev, open: true }));
              // 🔥 FIXED: Don't reset scroll position when opening - let it maintain naturally
            }}
            input={<OutlinedInput notched />}
            onClose={() => {
              setDropdownState((prev) => ({ ...prev, open: false }));
              // Reset scroll position only when closing material dropdown
              if (isMaterial) {
                materialScrollPositionRef.current = 0;
              }
            }}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(selected as string[]).length} selected
            </Box>
          )}
          MenuProps={{
            autoFocus: false,
            disableAutoFocusItem: true,
            disableScrollLock: true,
            variant: "menu",
            PaperProps: {
              style: {
                maxHeight: 260,
                maxWidth: 330,
                fontSize: '0.800rem',
              },
              onScroll: (e: React.UIEvent<HTMLDivElement>) => {
                e.stopPropagation();
                // 🔥 Save scroll position for material dropdown
                if (isMaterial) {
                  materialScrollPositionRef.current = e.currentTarget.scrollTop;
                }
              },
              // 🔥 Restore scroll position after render for material dropdown
              ref: isMaterial ? (node: HTMLDivElement | null) => {
                if (node && materialScrollPositionRef.current > 0) {
                  // Restore scroll position after a small delay to ensure content is rendered
                  setTimeout(() => {
                    node.scrollTop = materialScrollPositionRef.current;
                  }, 0);
                }
              } : undefined,
            },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            keepMounted: false,
          }}
          inputProps={{
            "aria-label": label,
          }}
        >
          {/* Search field */}
          <MenuItem
            disableRipple
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ "&:hover": { backgroundColor: "transparent" } }}
          >
            <TextField
              size="small"
              placeholder="Search..."
              value={dropdownState.search}
              onChange={(e) => onSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              fullWidth
            />
          </MenuItem>

          <Divider />

          {/* Select All Checkbox (only for material dropdown) */}
          {onSelectAll && !dropdownState.loading && options.length > 0 && (
            <>
              <MenuItem onClick={onSelectAll} disableRipple>
                <Checkbox checked={allSelected} indeterminate={someSelected} />
                <ListItemText
                  primary={isMaterial ? `Select All (${dropdownState.totalCount} total)` : "Select All"}
                  primaryTypographyProps={{
                    fontWeight: 600,
                  }}
                />
              </MenuItem>
              <Divider />
            </>
          )}

          {/* Loading state */}
          {dropdownState.loading && (
            <MenuItem disabled>
              <CircularProgress size={20} />
              <Typography sx={{ ml: 2 }}>Loading...</Typography>
            </MenuItem>
          )}

          {/* Empty state */}
          {!dropdownState.loading && options.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                No options found
              </Typography>
            </MenuItem>
          )}

          {/* Options list */}
          {!isMaterial
            ? (options as string[]).map((option) => (
                <Tooltip key={option} title={option} placement="right">
                  <MenuItem
                    value={option}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(option);
                    }}
                    sx={{
                      "& .MuiListItemText-root": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <Checkbox checked={selected.includes(option)} />
                    <ListItemText
                      primary={option}
                      primaryTypographyProps={{
                        sx: {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                  </MenuItem>
                </Tooltip>
              ))
            : (options as IMaterialDetail[]).map((option) => {
                // 🔥 NEW: Check if material is already added to the table
                const isAlreadyAdded = rows.some((row) => row.material === option.material);
                
                return (
                  <Tooltip
                    key={option.material}
                    title={isAlreadyAdded ? `${option.material} (Already Added)` : option.material}
                    placement="right"
                  >
                    <MenuItem
                      value={option.material}
                      disabled={isAlreadyAdded}
                      onClick={(e) => {
                        if (!isAlreadyAdded) {
                          e.stopPropagation();
                          onToggle(option.material);
                        }
                      }}
                      sx={{
                        "& .MuiListItemText-root": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                        opacity: isAlreadyAdded ? 0.5 : 1,
                      }}
                    >
                      <Checkbox 
                        checked={selected.includes(option.material)} 
                        disabled={isAlreadyAdded}
                      />
                      <ListItemText
                        primary={option.material}
                        secondary={isAlreadyAdded ? "Already Added" : undefined}
                        primaryTypographyProps={{
                          sx: {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            fontSize: "0.7rem",
                            color: "text.disabled",
                          },
                        }}
                      />
                    </MenuItem>
                  </Tooltip>
                );
              })}

          {/* Load more button */}
          {dropdownState.hasMore && !dropdownState.loading && (
            <>
              <Divider />
              <MenuItem onClick={onLoadMore} sx={{ justifyContent: "center" }}>
                <Button size="small" fullWidth>
                  Load More...
                </Button>
              </MenuItem>
            </>
          )}

          {/* Pagination info at bottom */}
          {!dropdownState.loading && options.length > 0 && (
            <>
              <Divider />
              <MenuItem disabled sx={{ justifyContent: "center", opacity: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Page {currentPage} of {totalPages} | Showing {currentCount} of{" "}
                  {dropdownState.totalCount}
                </Typography>
              </MenuItem>
            </>
          )}
        </Select>
        {onClear && selected.length > 0 && (
          <Tooltip title={`Clear ${label}`} placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              sx={{ 
                position: 'absolute',
                right: 32,
                top: 6,
                padding: '4px',
                zIndex: 1,
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'error.main',
                },
              }}
            >
              <FilterAltOffIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </FormControl>
      </Box>
    );
  };

  return (
    <>
      {/* Selection Controls - All in One Row */}
      <Box display="flex" gap={2} mt={2} alignItems="center">
        {renderDropdown(
          "Family",
          familyOptions,
          selectedFamilies,
          (value) =>
            handleFamilyChange(
              selectedFamilies.includes(value)
                ? selectedFamilies.filter((f) => f !== value)
                : [...selectedFamilies, value]
            ),
          familyDropdown,
          setFamilyDropdown,
          handleFamilySearch,
          handleLoadMoreFamilies,
          false,
          "21%",
          undefined,
          handleClearFamily
        )}

        {renderDropdown(
          "Group",
          groupOptions,
          selectedGroups,
          (value) =>
            handleGroupChange(
              selectedGroups.includes(value)
                ? selectedGroups.filter((g) => g !== value)
                : [...selectedGroups, value]
            ),
          groupDropdown,
          setGroupDropdown,
          handleGroupSearch,
          handleLoadMoreGroups,
          false,
          "21%",
          undefined,
          handleClearGroup
        )}

        {renderDropdown(
          "Subgroup",
          subgroupOptions,
          selectedSubgroups,
          (value) =>
            handleSubgroupChange(
              selectedSubgroups.includes(value)
                ? selectedSubgroups.filter((s) => s !== value)
                : [...selectedSubgroups, value]
            ),
          subgroupDropdown,
          setSubgroupDropdown,
          handleSubgroupSearch,
          handleLoadMoreSubgroups,
          false,
          "21%",
          undefined,
          handleClearSubgroup
        )}

        {renderDropdown(
          "Material",
          materialOptions,
          selectedMaterials,
          (value) =>
            handleMaterialChange(
              selectedMaterials.includes(value)
                ? selectedMaterials.filter((m) => m !== value)
                : [...selectedMaterials, value]
            ),
          materialDropdown,
          setMaterialDropdown,
          handleMaterialSearch,
          handleLoadMoreMaterials,
          true,
          "21%",
          handleSelectAllMaterials,
          handleClearMaterial
        )}

        <Tooltip title="Clear All Selections">
          <span>
            <IconButton
              size="small"
              onClick={handleClearAll}
              disabled={
                selectedFamilies.length === 0 &&
                selectedGroups.length === 0 &&
                selectedSubgroups.length === 0 &&
                selectedMaterials.length === 0
              }
              sx={{ 
                height: 40,
                width: 40,
                '&:hover': {
                  color: 'error.main',
                },
              }}
            >
              <FilterAltOffIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={selectedMaterials.length === 0}
          sx={{ flexBasis: "8%", minWidth: 0, height: 40 }}
        >
          {t("add")}
        </Button>
      </Box>

      {/* Materials Table */}
      <Box
        sx={{
          mt: 3,
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          maxHeight: 600,
          position: "relative",
        }}
      >
        {/* Sticky Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "white",
          }}
        >
          <Table
            sx={{
              tableLayout: "fixed",
            }}
            size="medium"
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5", height: 36 }}>
                <TableCell sx={{ fontWeight: 600, width: "23%", backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                  {t("product_family")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: "23%", backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                  {t("product_group")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: "23%", backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                  {t("product_subgroup")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: "23%", backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                  {t("material")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: "8%", backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                  {t("actions")}
                </TableCell>
              </TableRow>
              {/* Search row */}
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell sx={{ py: 1, backgroundColor: "#fafafa", borderBottom: "2px solid #e0e0e0" }}>
                  <TextField
                    size="small"
                    placeholder="Search family..."
                    value={tableFamilySearch}
                    onChange={(e) => setTableFamilySearch(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ backgroundColor: "white" }}
                  />
                </TableCell>
                <TableCell sx={{ py: 1, backgroundColor: "#fafafa", borderBottom: "2px solid #e0e0e0" }}>
                  <TextField
                    size="small"
                    placeholder="Search group..."
                    value={tableGroupSearch}
                    onChange={(e) => setTableGroupSearch(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ backgroundColor: "white" }}
                  />
                </TableCell>
                <TableCell sx={{ py: 1, backgroundColor: "#fafafa", borderBottom: "2px solid #e0e0e0" }}>
                  <TextField
                    size="small"
                    placeholder="Search subgroup..."
                    value={tableSubgroupSearch}
                    onChange={(e) => setTableSubgroupSearch(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ backgroundColor: "white" }}
                  />
                </TableCell>
                <TableCell sx={{ py: 1, backgroundColor: "#fafafa", borderBottom: "2px solid #e0e0e0" }}>
                  <TextField
                    size="small"
                    placeholder="Search material..."
                    value={tableMaterialSearch}
                    onChange={(e) => setTableMaterialSearch(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ backgroundColor: "white" }}
                  />
                </TableCell>
                <TableCell sx={{ backgroundColor: "#fafafa", borderBottom: "2px solid #e0e0e0" }} />
              </TableRow>
            </TableHead>
          </Table>
        </Box>

        {/* Scrollable Body */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 100,
          }}
        >
          <Table
            sx={{
              tableLayout: "fixed",
            }}
            size="medium"
          >
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, width: "23%" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("no_materials_added")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, width: "23%" }}>
                    <Typography variant="body2" color="text.secondary">
                      No materials match your search criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{
                      width: "23%",
                      maxWidth: "200px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      lineHeight: 1.2,
                    }}>
                      {row.productFamily || "-"}
                    </TableCell>
                    <TableCell sx={{
                      width: "23%",
                      maxWidth: "200px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      lineHeight: 1.2,
                    }}>
                      {row.productGroup || "-"}
                    </TableCell>
                    <TableCell sx={{
                      width: "23%",
                      maxWidth: "200px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      lineHeight: 1.2,
                    }}>
                      {row.productSubGroup || "-"}
                    </TableCell>
                    <TableCell sx={{
                      width: "23%",
                      maxWidth: "200px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      lineHeight: 1.2,
                    }}>
                      {row.material || "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ width: "8%" }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteRow(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Sticky Footer */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 3,
            backgroundColor: "#f5f5f5",
            borderTop: "2px solid #e0e0e0",
            py: 1.5,
            px: 2,
          }}
        >
          <Typography variant="body2" fontWeight={600} color="text.primary">
            Total Materials Assigned: {filteredRows.length}
            {filteredRows.length !== rows.length && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                (Filtered from {rows.length})
              </Typography>
            )}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default MaterialFormTable;
